import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Tooltip } from '@/components/Tooltip';

interface ChatGuideProps {
  onPromptClick?: (prompt: string) => void;
}

interface PromptCategory {
  title: string;
  icon: string;
  description: string;
  prompts: { label: string; query: string }[];
}

export const ChatGuide: React.FC<ChatGuideProps> = ({ onPromptClick }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [searchQuery, setSearchQuery] = useState('');

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const promptCategories: PromptCategory[] = [
    {
      title: 'Attack Surface Discovery',
      icon: '🎯',
      description:
        'Comprehensive external asset enumeration and exposure mapping',
      prompts: [
        {
          label: 'Internet-Facing Assets',
          query:
            'Show all internet-facing assets discovered through port scanning, DNS resolution, and subdomain enumeration. Include open ports, services, and technology fingerprints',
        },
        {
          label: 'DNS Intelligence',
          query:
            'Enumerate all DNS records, subdomains, and certificate transparency logs for our domains. Include historical DNS data and WHOIS information',
        },
        {
          label: 'Domain Expansion',
          query:
            'Perform reverse WHOIS lookups to discover related domains and infrastructure. Include registrant correlations and shared hosting analysis',
        },
        {
          label: 'Technology Fingerprinting',
          query:
            'Identify all technologies, frameworks, and services running across our web properties. Include version numbers, BuiltWith data, and favicon matching',
        },
        {
          label: 'TLS/SSL Certificate Analysis',
          query:
            'Collect and analyze all SSL/TLS certificates. Show certificate chains, subject alternative names, and shared certificate patterns',
        },
        {
          label: 'Web Application Discovery',
          query:
            'Discover all web applications, login pages, and authentication endpoints. Include screenshot captures and crawl results',
        },
        {
          label: 'Infrastructure Mapping',
          query:
            'Map complete infrastructure including CIDR blocks, IP ranges, ASN details, and network topology. Correlate with cloud providers',
        },
        {
          label: 'Third-Party Exposure',
          query:
            'Identify third-party integrations through CSP headers, analytics tracking, and reverse CSP lookups. Include vendor risk assessment',
        },
        {
          label: 'Organization Intelligence',
          query:
            'Enumerate organization details from SEC Edgar filings, person enumeration, and corporate structure analysis',
        },
      ],
    },
    {
      title: 'Cloud Security Assessment',
      icon: '☁️',
      description:
        'Multi-cloud security posture and misconfiguration detection',
      prompts: [
        {
          label: 'Public Cloud Resources',
          query:
            'Identify all publicly accessible cloud resources across AWS, Azure, and GCP. Flag exposed S3 buckets, storage accounts, and compute instances',
        },
        {
          label: 'AWS Security Posture',
          query:
            'Audit AWS environment for security issues: public EC2 instances, exposed Lambda functions, open security groups, and IAM misconfigurations',
        },
        {
          label: 'Azure Security Analysis',
          query:
            'Assess Azure resources for public exposure, storage account misconfigurations, VM vulnerabilities, and Azure AD security gaps',
        },
        {
          label: 'GCP Infrastructure Audit',
          query:
            'Review GCP security: public Compute Engine instances, Cloud Storage buckets, Cloud Functions exposure, Cloud Run services, and SQL databases',
        },
        {
          label: 'Cloud Secrets Detection',
          query:
            'Scan for exposed secrets in cloud environments: environment variables, parameter stores, key vaults, and configuration files',
        },
        {
          label: 'Serverless Security',
          query:
            'Audit serverless functions across AWS Lambda, Azure Functions, and GCP Cloud Functions for code vulnerabilities and configuration issues',
        },
        {
          label: 'Container Security',
          query:
            'Assess container security including exposed Docker registries, Kubernetes misconfigurations, and container image vulnerabilities',
        },
        {
          label: 'Cloud Storage Exposure',
          query:
            'Enumerate all cloud storage buckets and containers. Test for public read/write access, anonymous access, and data exposure risks',
        },
      ],
    },
    {
      title: 'Web Application Security',
      icon: '🌐',
      description:
        'OWASP-based vulnerability detection and penetration testing',
      prompts: [
        {
          label: 'Nuclei Vulnerability Scan',
          query:
            'Run comprehensive Nuclei template scanning across all web applications. Include CVE checks, misconfigurations, and known vulnerability patterns',
        },
        {
          label: 'Burp Suite Integration',
          query:
            'Show results from Burp Suite DAST scanning. Include injection vulnerabilities, authentication issues, and business logic flaws',
        },
        {
          label: 'SQL Injection Analysis',
          query:
            'Perform SQL injection reconnaissance across all web forms, API endpoints, and URL parameters. Identify injection points and vulnerable parameters',
        },
        {
          label: 'Authentication Security',
          query:
            'Test authentication mechanisms for login page vulnerabilities, default credentials, weak password policies, and session management issues',
        },
        {
          label: 'Web Secrets Exposure',
          query:
            'Scan web pages and JavaScript files for exposed API keys, credentials, tokens, and sensitive data in client-side code',
        },
        {
          label: 'API Security Testing',
          query:
            'Assess REST and GraphQL APIs for security issues: authentication bypass, authorization flaws, injection vulnerabilities, and excessive data exposure',
        },
        {
          label: 'Web Crawler Results',
          query:
            'Review comprehensive web crawl data for hidden endpoints, administrative interfaces, backup files, and sensitive information disclosure',
        },
        {
          label: 'Screenshot Analysis',
          query:
            'Analyze visual screenshots of web applications for exposed admin panels, error messages, and potential security indicators',
        },
      ],
    },
    {
      title: 'Code Security Analysis',
      icon: '📦',
      description: 'Source code security and repository scanning',
      prompts: [
        {
          label: 'GitHub Repository Scan',
          query:
            'Scan GitHub repositories for security issues: exposed secrets, vulnerable dependencies, insecure code patterns, and access misconfigurations',
        },
        {
          label: 'Repository Secrets',
          query:
            'Search for hardcoded credentials, API keys, private keys, and sensitive data in code repositories. Include historical commits analysis',
        },
        {
          label: 'Dependency Vulnerabilities',
          query:
            'Identify vulnerable dependencies and packages in our code repositories. Include CVE mappings and exploit availability',
        },
        {
          label: 'GitHub Device Code Flow',
          query:
            'Audit GitHub OAuth device code flow implementations for security weaknesses and token exposure risks',
        },
        {
          label: 'Open Source Intelligence',
          query:
            'Gather intelligence from public GitHub repositories including technology stack, development patterns, and potential security oversights',
        },
      ],
    },
    {
      title: 'Vulnerability Management',
      icon: '⚠️',
      description: 'Risk prioritization and exploit intelligence',
      prompts: [
        {
          label: 'Critical Vulnerabilities',
          query:
            'Show all critical vulnerabilities with CVSS scores above 9.0. Prioritize by internet exposure and exploit availability',
        },
        {
          label: 'Exploitable Vulnerabilities',
          query:
            'List vulnerabilities with public exploits, active exploitation (CISA KEV), or high EPSS scores. Include proof-of-concept availability',
        },
        {
          label: 'Internet-Exposed Risks',
          query:
            'Identify vulnerabilities on internet-facing assets that pose immediate breach risk. Prioritize by exploitability and business impact',
        },
        {
          label: 'Zero-Day Exposure',
          query:
            'Monitor for newly disclosed vulnerabilities affecting our technology stack. Track time-to-patch and vulnerability disclosure timelines',
        },
        {
          label: 'Patch Priority Matrix',
          query:
            'Generate prioritized patching roadmap based on CVSS, EPSS, asset criticality, and exploit availability. Include remediation timelines',
        },
        {
          label: 'Vulnerability Trends',
          query:
            'Analyze vulnerability trends over time: mean time to remediation, new vulnerability discovery rate, and security posture improvements',
        },
        {
          label: 'Compliance Gaps',
          query:
            'Map vulnerabilities to compliance requirements (PCI-DSS, SOC 2, HIPAA). Identify compliance violations and regulatory risk exposure',
        },
      ],
    },
    {
      title: 'Technology Intelligence',
      icon: '🔧',
      description: 'Technology stack analysis and version tracking',
      prompts: [
        {
          label: 'Technology Stack Overview',
          query:
            'Provide complete technology stack inventory: web frameworks, programming languages, databases, CDNs, and infrastructure components',
        },
        {
          label: 'Version Analysis',
          query:
            'Show software versions across all assets. Flag end-of-life technologies, unsupported versions, and upgrade requirements',
        },
        {
          label: 'Framework Vulnerabilities',
          query:
            'Identify vulnerabilities in detected frameworks and libraries. Cross-reference with CVE database for known security issues',
        },
        {
          label: 'Web Server Fingerprints',
          query:
            'Enumerate web server types, versions, and configurations. Include Apache, Nginx, IIS, and specialized web technologies',
        },
        {
          label: 'JavaScript Library Analysis',
          query:
            'Analyze client-side JavaScript libraries for outdated versions, known vulnerabilities, and security best practices',
        },
        {
          label: 'Third-Party Services',
          query:
            'Map all third-party services and integrations: analytics platforms, CDNs, payment processors, and external dependencies',
        },
        {
          label: 'DNS Provider Analysis',
          query:
            'Identify DNS providers, registrars, and nameserver configurations. Assess for DNS security and availability risks',
        },
        {
          label: 'Analytics & Tracking',
          query:
            'Enumerate analytics tracking codes, advertising networks, and monitoring services across all web properties',
        },
      ],
    },
    {
      title: 'Continuous Monitoring',
      icon: '📡',
      description: 'Real-time security monitoring and change detection',
      prompts: [
        {
          label: 'Attack Surface Changes',
          query:
            'Monitor attack surface expansion: new domains, subdomains, open ports, and newly discovered assets. Track infrastructure growth over time',
        },
        {
          label: 'Configuration Drift',
          query:
            'Detect security configuration changes: modified DNS records, new TLS certificates, altered firewall rules, and service changes',
        },
        {
          label: 'New Vulnerability Detection',
          query:
            'Track newly discovered vulnerabilities affecting our assets. Alert on zero-day disclosures and critical security updates',
        },
        {
          label: 'Public Exposure Monitoring',
          query:
            'Monitor for newly exposed assets, services, and resources. Track changes from private to public accessibility',
        },
        {
          label: 'Cloud Resource Changes',
          query:
            'Detect new cloud resources, modified IAM policies, and infrastructure changes across AWS, Azure, and GCP environments',
        },
        {
          label: 'Certificate Expiration',
          query:
            'Track SSL/TLS certificate expiration dates. Alert on certificates expiring within 30 days and self-signed certificates',
        },
        {
          label: 'Shadow IT Detection',
          query:
            'Identify unauthorized cloud resources, rogue web applications, and undocumented infrastructure changes',
        },
        {
          label: 'Breach Intelligence',
          query:
            'Monitor for credential exposure in breach databases, paste sites, and dark web sources. Track company-related security incidents',
        },
      ],
    },
    {
      title: 'Executive Reporting',
      icon: '📊',
      description: 'CISO-level risk quantification and strategic insights',
      prompts: [
        {
          label: 'Security Posture Summary',
          query:
            'Generate executive summary: total assets, critical vulnerabilities, risk score trends, and key security metrics. Include comparison to previous periods',
        },
        {
          label: 'Risk Quantification',
          query:
            'Quantify cyber risk exposure with financial impact modeling. Calculate potential breach costs, business disruption, and regulatory penalties',
        },
        {
          label: 'Board-Level Metrics',
          query:
            'Prepare board-ready security metrics: attack surface size, vulnerability remediation velocity, mean time to detect/respond, and security investment ROI',
        },
        {
          label: 'Compliance Status',
          query:
            'Report compliance posture across regulatory frameworks: PCI-DSS, SOC 2, HIPAA, GDPR. Highlight gaps and remediation requirements',
        },
        {
          label: 'Industry Benchmarking',
          query:
            'Compare our security posture to industry peers. Show percentile rankings for vulnerability density, patch velocity, and security maturity',
        },
        {
          label: 'Remediation Roadmap',
          query:
            'Generate strategic remediation roadmap with prioritized initiatives, resource requirements, and expected risk reduction timelines',
        },
        {
          label: 'Threat Exposure Analysis',
          query:
            'Assess exposure to current threat landscape: ransomware susceptibility, targeted attack vectors, and adversary capability mapping',
        },
        {
          label: 'M&A Security Diligence',
          query:
            'Provide security assessment for merger and acquisition due diligence: technical debt, security gaps, and integration risk factors',
        },
      ],
    },
    {
      title: 'Asset & Risk Queries',
      icon: '🗂️',
      description: 'Asset inventory and risk-based filtering',
      prompts: [
        {
          label: 'High-Risk Assets',
          query:
            'Show assets with highest risk scores. Include internet-facing systems with critical vulnerabilities and business-critical services',
        },
        {
          label: 'Asset Inventory by Type',
          query:
            'Provide complete asset inventory categorized by type: domains, IP addresses, web applications, cloud resources, and code repositories',
        },
        {
          label: 'Unmonitored Assets',
          query:
            'Identify assets lacking continuous monitoring, outdated scan data, or incomplete security coverage',
        },
        {
          label: 'Critical Infrastructure',
          query:
            'List business-critical assets including production systems, customer-facing applications, and revenue-generating infrastructure',
        },
        {
          label: 'Asset Relationships',
          query:
            'Map asset relationships and dependencies: domain ownership, subdomain hierarchies, IP allocations, and infrastructure correlations',
        },
        {
          label: 'Legacy Infrastructure',
          query:
            'Identify legacy systems, end-of-life technologies, and deprecated infrastructure requiring modernization or decommissioning',
        },
        {
          label: 'External Attack Vectors',
          query:
            'Enumerate all external attack vectors: exposed services, authentication endpoints, API gateways, and remote access points',
        },
        {
          label: 'Data Flow Analysis',
          query:
            'Analyze data flows between assets: API connections, database relationships, and service-to-service communications',
        },
      ],
    },
    {
      title: 'Network & Infrastructure',
      icon: '🌐',
      description: 'Network security analysis and infrastructure intelligence',
      prompts: [
        {
          label: 'Port & Service Analysis',
          query:
            'Show all open ports and running services across our infrastructure. Flag uncommon ports, legacy protocols, and unnecessary exposure',
        },
        {
          label: 'Network Segmentation',
          query:
            'Assess network segmentation effectiveness. Identify flat network architectures and assets lacking proper isolation',
        },
        {
          label: 'CIDR Block Intelligence',
          query:
            'Map all owned CIDR blocks and IP address ranges. Include geolocation data, hosting providers, and autonomous system numbers',
        },
        {
          label: 'TLS Configuration Review',
          query:
            'Audit TLS/SSL configurations for weak cipher suites, protocol vulnerabilities, and certificate issues. Test for POODLE, BEAST, and Heartbleed',
        },
        {
          label: 'DNS Security Analysis',
          query:
            'Review DNS security: DNSSEC implementation, zone transfer vulnerabilities, subdomain takeover risks, and DNS hijacking prevention',
        },
        {
          label: 'CDN & Load Balancer Config',
          query:
            'Analyze CDN and load balancer configurations for security issues: origin exposure, cache poisoning risks, and SSL/TLS termination',
        },
        {
          label: 'Hosting Provider Analysis',
          query:
            'Identify hosting providers, cloud platforms, and data center locations. Assess geographic distribution and infrastructure redundancy',
        },
        {
          label: 'WHOIS & Domain Intelligence',
          query:
            'Gather domain intelligence from WHOIS records: registration dates, registrant details, nameservers, and ownership correlations',
        },
      ],
    },
  ];

  // Filter prompts based on search query
  const filteredCategories = promptCategories
    .map(category => ({
      ...category,
      prompts: category.prompts.filter(
        prompt =>
          searchQuery === '' ||
          prompt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          prompt.query.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter(category => category.prompts.length > 0);

  return (
    <div>
      {/* Search Bar */}
      <div className="p-1 pb-2">
        <div className="relative">
          <MagnifyingGlassIcon className="text-chariot-text-tertiary absolute top-1/2 left-3 size-4 -translate-y-1/2 transform" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search prompts..."
            className="bg-chariot-transparent text-chariot-text-primary placeholder:text-chariot-text-secondary w-full rounded-md border-0 p-3 px-10 py-2 text-sm"
          />
        </div>
      </div>

      {/* Prompt Categories */}
      <div className="px-2 py-2">
        {filteredCategories.map(category => {
          // Auto-expand categories when searching
          const isExpanded =
            searchQuery !== '' || expandedCategories.has(category.title);

          return (
            <div
              key={category.title}
              className="border-chariot-stroke/50 border-b last:border-0"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.title)}
                className="hover:text-chariot-brand-primary group flex w-full items-center justify-between py-2 text-left transition-colors"
              >
                <span className="text-chariot-text-primary group-hover:text-chariot-brand-primary text-sm font-semibold">
                  {category.title}
                </span>
                <ChevronRightIcon
                  className={`text-chariot-text-tertiary size-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              </button>

              {/* Category Prompts */}
              {isExpanded && (
                <div className="space-y-0.5 pb-1.5">
                  {category.prompts.map((prompt, idx) => (
                    <Tooltip key={idx} title={prompt.query} placement="bottom">
                      <button
                        onClick={() => onPromptClick?.(prompt.query)}
                        className="text-chariot-text-secondary hover:text-chariot-brand-primary hover:bg-chariot-background-secondary/30 w-full rounded py-1 pl-2 text-left text-xs transition-colors"
                      >
                        {prompt.label}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChatGuide;
